/**
 * ./src/modules/product/components/samples/SamplesHits.tsx
 *
 * SamplesHits component to display the Samples hits
 *
 * @copyright DesignShop, 2024
 * @since 06/04/2024
 */
import React, { Suspense, createRef, useMemo } from 'react';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import useIsMobile from '@/modules/common/hooks/useIsMobile';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import { useFinder } from '@/modules/cora/context/FinderProvider';
import { useVisibleItemsTracking } from '@/modules/product/hooks/useVisibleItemsTracking';
import { ProductPdpInterface } from '@/modules/product/models/product-pdp.interface';
import { DataLayerFeature } from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { Filter as FiltersDefinitionItemType } from '@/modules/product/services/filtersTransform';
import { SampleCard } from '@/modules/sample-card';
import { SampleCardSkeleton } from '@/modules/sample-card';
import { selectShoppingListProducts } from '@/modules/shopping-list/state/shoppingListSlice';

import { useCurrentFilters } from '../../hooks/useCurrentFilters';
import useGridLayout from '../../hooks/useGridLayout';

type SampleHitsProps = {
  pageTitle: string;
  roomSlug?: string;
  hasSceneOpen?: boolean;
  publishedProducts: ProductPdpInterface[];
};

const SamplesHits = React.memo(
  ({ pageTitle, roomSlug, hasSceneOpen, publishedProducts }: SampleHitsProps) => {
    const shoppingListProducts = useAppSelector(selectShoppingListProducts);
    const productRefs = useMemo(
      () => publishedProducts.map(() => createRef<HTMLDivElement>()),
      [publishedProducts]
    );
    const { gridLayout } = useGridLayout();
    const isMobile = useIsMobile('sm');
    const viewMode = gridLayout === 'side-by-side' && isMobile ? 'compact' : 'default';
    const { items: currentFilters } = useCurrentFilters();
    const { filtersDefinition } = useFinder();

    const attributeToLabelMap = useMemo(() => {
      const map = new Map<string, string>();
      filtersDefinition.forEach((def: FiltersDefinitionItemType) => {
        if (def.attribute && def.label) {
          map.set(def.attribute, def.label);
        }
      });
      return map;
    }, [filtersDefinition]);

    const applied_filters = useMemo(
      () =>
        currentFilters.map((item) => ({
          type: attributeToLabelMap.get(item.attribute) || item.attribute,
          value: item.name,
        })),
      [currentFilters, attributeToLabelMap]
    );

    useVisibleItemsTracking({
      products: publishedProducts,
      productRefs,
      shoppingListProducts,
      feature: DataLayerFeature.PLP,
      where: DataLayerWhere.Search,
      itemListId: roomSlug || 'Department-PLP',
      itemListName: pageTitle || 'Department Products',
      applied_filters,
    });

    return (
      <Suspense fallback={<SampleHitsSkeleton />}>
        {publishedProducts.map((product, index) => {
          return (
            <li
              key={`hit-${index}-${product.sku}`}
              data-testid={`hit-${index}`}
              className="flex flex-col"
            >
              <div
                ref={productRefs[index]}
                data-product-id={product.id}
                data-index={index}
                className="flex h-full flex-col"
              >
                <SampleCard
                  {...product}
                  index={index}
                  where={DataLayerWhere.ProductCard}
                  item_list_id={roomSlug || 'Department-PLP'}
                  item_list_name={pageTitle || 'Department Products'}
                  item_list_type={'PLP'}
                  showProductType={false}
                  hasSceneOpen={hasSceneOpen}
                  data-testid={`sample-card-${index}`}
                  feature={DataLayerFeature.PLP}
                  viewMode={viewMode}
                />
              </div>
            </li>
          );
        })}
      </Suspense>
    );
  }
);

SamplesHits.displayName = 'SamplesHits';

export default SamplesHits;

export const SampleHitsSkeleton = () => {
  return Array.from({ length: 20 }).map((_, index) => (
    <SampleCardSkeleton key={`hit-${index}`} data-testid={`skeleton-hit-${index}`} />
  ));
};
